import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { Z_LAYERS } from '../../../game/rendering/Z_LAYERS';
import { biomeCloser } from '../presetBuilders';

/**
 * Nexus — the Biome Nexus hub. Ethereal violet concourse (gap survey §2: "the hub has no sense
 * of place"): dais/ceiling glow blobs for depth + a drifting violet light field + a focal
 * breathing bloom vortex over the central dais (world x≈1200, top y=740) + pale motes rising
 * from it.
 */
export const NEXUS: AtmospherePreset = {
  id: 'nexus',
  layers: [
    // Ethereal violet backplane (PX3) — dark violet void → soft violet glow at the base.
    { kind: 'skyGradient', from: 0x0a0618, to: 0x241848 },
    {
      kind: 'glowBlobs',
      blobs: [
        { color: P.nexusViolet, atX: 0.5, atY: 0.78, size: 660, alpha: 0.1 }, // dais underglow
        { color: P.crystal, atX: 0.5, atY: 0.1, size: 520, alpha: 0.07 }, // high ambient bloom
      ],
      // The vortex glow breathes its HUE — a slow `tint` modulator lerps both blobs
      // violet → arcane cyan and back, so the Nexus feels alive (the tint-target demo).
      tintTo: P.cyan,
      modulators: [{ target: 'tint', shape: 'sine', periodMs: 7000, min: 0, max: 0.7 }],
    },
    {
      kind: 'lightField',
      count: 20, palette: [P.nexusViolet, P.frostCore, P.mint, P.softPink],
      sizeMin: 90, sizeMax: 230, alphaMin: 0.05, alphaMax: 0.2,
      driftRadius: 70, periodMinMs: 9000, periodMaxMs: 17000,
    },
    {
      kind: 'fireflies', textureKey: 'sparkle', // ethereal star-motes adrift in the concourse
      count: 16, colors: [P.frostCore, P.mint, P.softPink, P.crystal],
      sizeMin: 8, sizeMax: 18, driftPx: 28,
      periodMinMs: 6000, periodMaxMs: 12000, alphaMin: 0.18, alphaMax: 0.7,
    },
    {
      kind: 'bloom',
      x: 1200, y: 560, // floating above the dais (center 1200, top 740)
      color: P.nexusViolet, size: 460,
      alphaMin: 0.1, alphaMax: 0.36, scaleMin: 0.85, scaleMax: 1.3, periodMs: 4600,
    },
    {
      kind: 'weather',
      x: 1200, y: 745, // rising from the dais top
      color: P.nexusMote, size: 22,
      frequencyMs: 240, lifespanMs: 5200, speed: 26, alphaEnd: 0.5,
    },
    // Faint ethereal depth (PX3) — distant violet spires give the concourse a horizon
    // without breaking the floating-void feel (low alpha, hazy, slow sway). Both bands
    // sit on the scene horizon (y=0 lift, scene-schema); far = TALLER behind, near shorter.
    { kind: 'parallax', svg: OTHERWORLD_BACKDROPS.celestial.far, shape: 'spires', color: 0x2e2452, speed: 0.03, y: 0, height: 380, alpha: 0.42,
      depth: Z_LAYERS.PARALLAX_FAR, detail: 0.8, sway: { ampPx: 10, periodMs: 36000 } },
    { kind: 'parallax', svg: OTHERWORLD_BACKDROPS.celestial.mid, shape: 'spires', color: 0x241a42, speed: 0.08, y: 0, height: 260, alpha: 0.55,
      depth: Z_LAYERS.PARALLAX_MID, detail: 1 },
    // Final colour-grade — violet-magenta lift matching the vortex palette.
    ...biomeCloser({ grade: { top: 0x6a4fff, bottom: 0x2a1a55, strength: 0.14 } }),
  ],
};
